// Meeting history (F-PLAT-5): the organizer's past meetings, built entirely
// from webhooks. No content is ever shown — titles appear only because the
// organizer typed them.
//
// Finding an old meeting: you remember WHO was there and roughly WHEN, not what
// the room was called — and an instant meeting has no name at all. So search
// matches participants as well as titles, and a DATE sits beside it.
//
// One date, not a range: the question is "which day was that meeting", and a
// from/to pair asked for two answers where people have one. It is applied
// server-side, because the unfiltered list is capped and filtering a capped page
// would quietly answer "nothing that day".

import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { api, MeetingHistoryEntry } from '../api';
import { DateField } from '../components/DateField';
import { Avatar } from '../ui/Avatar';
import { IconClose } from '../ui/icons';

/** Unfiltered, the server returns this many — stated in the UI rather than
 * left for someone to discover by missing a meeting. */
const RECENT_CAP = 50;

/** What an untitled (instant) meeting is called on screen. Showing the room
 * code instead was the reason search felt useless: nobody remembers
 * "mqd-rpxb-dgf". */
const UNTITLED = 'Instant meeting';

export function HistoryPage() {
  const navigate = useNavigate();
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; rows: MeetingHistoryEntry[] }
  >({ kind: 'loading' });
  const [q, setQ] = useState('');
  const [date, setDate] = useState('');
  const onQ = useCallback((e: ChangeEvent<HTMLInputElement>) => setQ(e.target.value), []);
  const clearDate = useCallback(() => setDate(''), []);
  const goBack = useCallback(() => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    api
      // One day: the same bound at both ends, which the server reads as the
      // whole of that day.
      .meetingHistory(date ? { from: date, to: date } : undefined)
      .then((rows) => {
        if (!cancelled) setState({ kind: 'ready', rows });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'error' });
      });
    return () => {
      cancelled = true; // a slow first response must not overwrite a newer range
    };
  }, [date]);

  const groups = useMemo(() => {
    if (state.kind !== 'ready') return [];
    const ql = q.trim().toLowerCase();
    const rows = state.rows.filter((m) => {
      if (ql === '') return true;
      if ((m.title ?? UNTITLED).toLowerCase().includes(ql)) return true;
      // Who was in the room — the way people actually remember a meeting.
      if (m.attendees.some((a) => (a.name || a.identity).toLowerCase().includes(ql))) return true;
      // The room code still matches, quietly: it is never shown, but someone
      // holding a link or a support ticket can still find the meeting.
      return m.slug.toLowerCase().includes(ql);
    });
    return groupByDay(rows);
  }, [state, q]);

  return (
    <div className="bz-hist">
      <header className="bz-hist-header">
        <div className="bz-hist-head-left">
          <button type="button" className="bz-hist-back" onClick={goBack} aria-label="Back">
            <BackIcon />
          </button>
          <h1>Meeting history</h1>
        </div>
        <div className="bz-hist-tools">
          <div className="bz-hist-search">
            <SearchIcon />
            <input
              type="search"
              placeholder="Search by title or person"
              value={q}
              onChange={onQ}
              aria-label="Search meetings by title or person"
            />
          </div>
          <div className={date ? 'bz-hist-date bz-hist-date-set' : 'bz-hist-date'}>
            <DateField value={date} onChange={setDate} direction="past" placeholder="Any date" />
            {date && (
              <button
                type="button"
                className="bz-hist-date-clear"
                onClick={clearDate}
                aria-label="Clear date"
              >
                <IconClose size={14} />
              </button>
            )}
          </div>
        </div>
      </header>

      {state.kind === 'ready' && (
        <p className="bz-hist-scope">
          {date
            ? `Showing meetings on ${dayLabel(date)}.`
            : `Showing your ${RECENT_CAP} most recent meetings — pick a date to look further back.`}
        </p>
      )}

      {state.kind === 'loading' && <p className="bz-hist-note">Loading…</p>}
      {state.kind === 'error' && (
        <p className="bz-hist-note bz-error-text">Could not load your history.</p>
      )}
      {state.kind === 'ready' && groups.length === 0 && (
        <p className="bz-hist-note">
          {q
            ? 'No meetings match your search.'
            : date
              ? 'No meetings on that date.'
              : 'No past meetings yet.'}
        </p>
      )}

      {groups.map((g) => (
        <section key={g.label} className="bz-hist-group">
          <h2 className="bz-hist-group-label">{g.label}</h2>
          <div className="bz-hist-rows">
            {g.rows.map((m) => (
              <MeetingRow key={m.slug} m={m} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/** "21 Aug" — how the chosen day reads back in the caption. */
function dayLabel(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
  });
}

function MeetingRow({ m }: { m: MeetingHistoryEntry }) {
  const { time, ampm } = clockParts(m.createdAt);
  const people = uniqueAttendees(m.attendees);
  const shown = people.slice(0, 3);
  const extra = people.length - shown.length;

  return (
    <div className="bz-hist-row">
      <div className="bz-hist-time">
        <span className="bz-hist-time-h">{time}</span>
        <span className="bz-hist-time-ampm">{ampm}</span>
      </div>

      <div className="bz-hist-body">
        <div className="bz-hist-titleline">
          <span className="bz-hist-title">{m.title ?? UNTITLED}</span>
          {m.recordingAvailable && <span className="bz-hist-badge bz-hist-badge-rec">REC</span>}
          {!!m.fileCount && (
            <span className="bz-hist-badge bz-hist-badge-doc">
              <DocIcon /> {m.fileCount}
            </span>
          )}
        </div>
        <div className="bz-hist-meta">
          {fmtDuration(m.durationSeconds)} · {people.length}{' '}
          {people.length === 1 ? 'participant' : 'participants'}
          {m.recordingAvailable && ' · Recording available'}
        </div>
      </div>

      {people.length > 0 && (
        <div className="bz-hist-avatars">
          {shown.map((a) => (
            <Avatar key={a.identity} name={a.name || a.identity} identity={a.identity} size={30} />
          ))}
          {extra > 0 && <span className="bz-hist-avatar-more">+{extra}</span>}
        </div>
      )}
    </div>
  );
}

// ── grouping / formatting ────────────────────────────────────────────────────

interface Group {
  label: string;
  rows: MeetingHistoryEntry[];
}

function groupByDay(rows: MeetingHistoryEntry[]): Group[] {
  const sorted = [...rows].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dayMs = 86_400_000;
  const order: string[] = [];
  const byLabel = new Map<string, MeetingHistoryEntry[]>();

  for (const m of sorted) {
    const d = new Date(m.createdAt);
    const start = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const diffDays = Math.round((startOfToday - start) / dayMs);
    let label: string;
    if (diffDays <= 0) label = `Today · ${shortDate(d)}`;
    else if (diffDays === 1) label = `Yesterday · ${shortDate(d)}`;
    else if (diffDays <= 7) label = 'Last week';
    else label = 'Earlier';
    if (!byLabel.has(label)) {
      byLabel.set(label, []);
      order.push(label);
    }
    byLabel.get(label)!.push(m);
  }
  return order.map((label) => ({ label, rows: byLabel.get(label)! }));
}

function shortDate(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }).toUpperCase();
}

function clockParts(iso: string): { time: string; ampm: string } {
  const d = new Date(iso);
  const raw = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true });
  const mm = /^(.*?)\s*(AM|PM)?$/i.exec(raw);
  return { time: mm?.[1]?.trim() ?? raw, ampm: (mm?.[2] ?? '').toUpperCase() };
}

function uniqueAttendees(attendees: MeetingHistoryEntry['attendees']): {
  identity: string;
  name: string;
}[] {
  const seen = new Map<string, { identity: string; name: string }>();
  for (const a of attendees) {
    if (!seen.has(a.identity)) seen.set(a.identity, { identity: a.identity, name: a.name });
  }
  return [...seen.values()];
}

function fmtDuration(seconds: number): string {
  if (seconds <= 0) return 'no sessions';
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h} hr ${rem} min` : `${h} hr`;
}

function BackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M19 12H5M12 19l-7-7 7-7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.5" />
      <path d="M20 20l-3.2-3.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function DocIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M14 3v5h5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}
